import React, {
 useEffect, useState, useRef, Fragment
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import SnmpDestinationForm, {
  Ref
} from 'routes/SnmpDestination/components/SnmpDestinationForm';
import * as snmpDestActions from 'data/snmp/snmp.actions';
import * as snmpDestSelectors from 'data/snmp/snmp.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { snmpDestTemplate } from 'routes/SnmpDestination/templates/snmpDestTemplate';
import {
 adaptTemplateOptions, adaptTemplateVisibilityMode, adaptTemplateOnLoad, adaptSnmpDestTemplate
} from 'routes/SnmpDestination/utils/snmpDestTemplate.adapter';
import { SnmpDestination, SnmpDestinationUI } from 'data/snmp/snmp.types';
import { adaptSnmpDestPayload } from 'data/snmp/snmp.adapter';
import { FieldUpdateHandlerParam } from 'shared/form';
import { getAuditDetailsToSubmit } from 'routes/SnmpDestination/utils/snmpDestTemplateDataToSubmit';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedSnmpDest: snmpDestSelectors.getSelectedSnmpDest(state),
  snmpDest: snmpDestSelectors.getSnmpDest(state),
  snmpUsers: snmpDestSelectors.getSnmpUsers(state),
  snmpDestTemplateDef: adaptTemplateVisibilityMode(snmpDestTemplate, 'edit')
});

const mapDispatch = {
  goToSmnpDestList: snmpDestActions.goToSmnpDestList,
  updateSnmpDest: snmpDestActions.updateSnmpDest,
  createSnmpDest: snmpDestActions.createSnmpDest,
  getSnmpDest: snmpDestActions.getSnmpDest,
  goToSnmpDestEdit: snmpDestActions.goToSnmpDestEdit,
  getSnmpUsers: snmpDestActions.getSnmpUsers
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpDestEdit = ({
  history,
  userId,
  userName,
  isAuditEnabled,
  deliveryFunctionId,
  goToSmnpDestList,
  snmpDestTemplateDef,
  updateSnmpDest,
  goToSnmpDestEdit,
  getSnmpDest,
  getSnmpUsers,
  snmpDest,
  snmpUsers,
  selectedSnmpDest
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SnmpDestination>>({});
  const [template, setTemplate] = useState(snmpDestTemplateDef);
  const { t } = useTranslation();

  const id = selectedSnmpDest?.id;

  useEffect(() => {
    if (id) {
      getSnmpDest(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getSnmpDest]);

  useEffect(() => {
    getSnmpUsers(deliveryFunctionId);
  }, [deliveryFunctionId, getSnmpUsers]);

  useEffect(() => {
    if (snmpDest == null || snmpDestTemplateDef == null) {
      return;
    }

    const clonedSnmpDest = cloneDeep(snmpDest);
    let clonedTemplate = cloneDeep(snmpDestTemplateDef);
    clonedTemplate.formId = Date.now().toString();

    clonedTemplate = adaptTemplateOptions(clonedTemplate, snmpUsers);
    clonedTemplate = adaptTemplateOnLoad(snmpDest, clonedTemplate);

    setTemplate(clonedTemplate);
    setFormData(clonedSnmpDest);
  }, [snmpDest, snmpDestTemplateDef, snmpUsers]);

  const handleClickSave = () => {
    const payload = getSnmpDestPayload();
    if (payload && snmpDest !== null) {
      updateSnmpDest(deliveryFunctionId, payload, history);
    }
  };

  const getSnmpDestPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const snmpUserFormData: SnmpDestinationUI = formRef.current.getValues();

    const payload = {
      data: adaptSnmpDestPayload(snmpUserFormData),
      auditDetails: getAuditDetailsToSubmit(
        snmpUserFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE'
      )
    };
    return payload;
  };

  const handleClickEdit = () => goToSnmpDestEdit(history);

  const handleCancel = () => goToSmnpDestList(history);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SnmpDestination);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value
    };

    clonedTemplate = adaptSnmpDestTemplate(clonedTemplate, fieldName, value);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <SnmpDestinationForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (snmpDest == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="SnmpDestEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('snmpDest.update.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SnmpDestEdit));
